import type { Book } from './types';

export const BOOKS_TIME_ZONE = 'America/Los_Angeles';
const HISTOGRAM_DAYS = 21;
const DAY_MS = 24 * 60 * 60 * 1000;
export const BOOK_COLOR_PALETTE = [
  '#4f46e5',
  '#0ea5e9',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#14b8a6',
  '#ec4899',
];

export interface HistogramDayContribution {
  bookId: string;
  title: string;
  color: string;
  pages: number;
}

export interface HistogramDay {
  isoDate: string;
  label: string;
  totalPages: number;
  contributions: HistogramDayContribution[];
}

export interface HistogramSummary {
  days: HistogramDay[];
  legend: Array<{ bookId: string; title: string; color: string; totalPages: number }>;
  maxTotalPages: number;
}

const romanNumerals: Array<[number, string]> = [
  [1000, 'M'],
  [900, 'CM'],
  [500, 'D'],
  [400, 'CD'],
  [100, 'C'],
  [90, 'XC'],
  [50, 'L'],
  [40, 'XL'],
  [10, 'X'],
  [9, 'IX'],
  [5, 'V'],
  [4, 'IV'],
  [1, 'I'],
];

const HEX_COLOR_RE = /^#[0-9a-f]{6}$/i;
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
export const DEFAULT_BOOK_COLOR = BOOK_COLOR_PALETTE[0];
const pacificDateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: BOOKS_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const startOfDay = (value: Date): Date => new Date(value.getFullYear(), value.getMonth(), value.getDate());

const isoDate = (value: Date): string => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseIsoDate = (value: string): Date | null => {
  const match = ISO_DATE_RE.exec(value);
  if (match) {
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day), 12);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const getCurrentPacificDate = (): string => {
  const parts = pacificDateFormatter.formatToParts(new Date());
  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  const day = parts.find((part) => part.type === 'day')?.value;
  if (!year || !month || !day) {
    return isoDate(new Date());
  }
  return `${year}-${month}-${day}`;
};

export const formatRoman = (value: number): string => {
  if (value <= 0) return '0';
  let remaining = Math.floor(value);
  let rendered = '';
  for (const [amount, numeral] of romanNumerals) {
    while (remaining >= amount) {
      rendered += numeral;
      remaining -= amount;
    }
  }
  return rendered.toLowerCase();
};

export const formatShortDate = (value: string): string => {
  const parsed = parseIsoDate(value);
  if (!parsed) return value;
  return parsed.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

export const formatLongDate = (value: string): string => {
  const parsed = parseIsoDate(value);
  if (!parsed) return value;
  return parsed.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
};

export const formatBookDisplay = (book: Book, position = book.current_position): string => {
  if (position <= 0) return 'Start';
  if (position <= book.intro_pages) return formatRoman(position);
  return `${position - book.intro_pages}`;
};

export const formatBookProgressLabel = (book: Book, position = book.current_position): string =>
  position > 0 && position <= book.intro_pages ? `Intro ${formatBookDisplay(book, position)}` : `p.${formatBookDisplay(book, position)}`;

export const formatProgressDelta = (delta: number): string => {
  if (delta === 0) return 'No change';
  const pages = `${Math.abs(delta)} page${Math.abs(delta) === 1 ? '' : 's'}`;
  return `${delta > 0 ? '+' : '-'}${pages}`;
};

export const clampBookPosition = (book: Book, nextPosition: number): number =>
  Math.min(Math.max(nextPosition, 0), book.total_positions);

export const getDefaultBookColor = (seed: string): string => {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) >>> 0;
  }
  return BOOK_COLOR_PALETTE[hash % BOOK_COLOR_PALETTE.length];
};

export const normalizeBookColor = (color: string): string =>
  HEX_COLOR_RE.test(color.trim()) ? color.trim().toLowerCase() : DEFAULT_BOOK_COLOR;

export const getBookActiveEnd = (book: Book): string =>
  book.completed_at && book.completed_at < book.target_date ? book.completed_at : book.target_date;

export const buildHistogramData = (books: Book[], days = HISTOGRAM_DAYS): HistogramSummary => {
  const pacificToday = parseIsoDate(getCurrentPacificDate()) ?? new Date();
  const today = startOfDay(pacificToday);
  const dayMap = new Map<string, Map<string, number>>();
  const legendTotals = new Map<string, number>();

  for (let offset = days - 1; offset >= 0; offset -= 1) {
    const date = new Date(today.getTime() - offset * DAY_MS);
    dayMap.set(isoDate(date), new Map());
  }

  for (const book of books) {
    for (const entry of book.history) {
      if (entry.pages_read <= 0) continue;
      const targetDay = dayMap.get(entry.date);
      if (!targetDay) continue;
      targetDay.set(book.id, (targetDay.get(book.id) ?? 0) + entry.pages_read);
      legendTotals.set(book.id, (legendTotals.get(book.id) ?? 0) + entry.pages_read);
    }
  }

  const bookById = new Map(books.map((book) => [book.id, book]));
  const renderedDays = Array.from(dayMap.entries()).map(([day, totals]) => {
    const date = parseIsoDate(day) ?? new Date(`${day}T12:00:00`);
    const contributions = Array.from(totals.entries())
      .map(([bookId, pages]) => {
        const book = bookById.get(bookId);
        if (!book) return null;
        return {
          bookId,
          title: book.title,
          color: book.color,
          pages,
        };
      })
      .filter((value): value is HistogramDayContribution => value !== null)
      .sort((left, right) => right.pages - left.pages);

    return {
      isoDate: day,
      label: date.toLocaleDateString([], { weekday: 'narrow' }),
      totalPages: contributions.reduce((sum, entry) => sum + entry.pages, 0),
      contributions,
    };
  });

  return {
    days: renderedDays,
    legend: books
      .map((book) => ({
        bookId: book.id,
        title: book.title,
        color: book.color,
        totalPages: legendTotals.get(book.id) ?? 0,
      }))
      .filter((entry) => entry.totalPages > 0)
      .sort((left, right) => right.totalPages - left.totalPages),
    maxTotalPages: Math.max(...renderedDays.map((day) => day.totalPages), 0),
  };
};

export const bookIsActiveOnDate = (book: Book, day: Date): boolean => {
  const dayKey = isoDate(day);
  const end = getBookActiveEnd(book);
  return book.start_date <= dayKey && dayKey <= end;
};

export const getVisibleBooks = (books: Book[], showCompleted: boolean): Book[] =>
  books.filter((book) => showCompleted || book.status !== 'completed');
